/**
 * Keeps a focused control clear of the table's sticky chrome.
 *
 * The browser scrolls a newly focused element into the scrollport, but it does
 * not know that pinned columns, the selection and actions columns, and the
 * header row paint over part of that scrollport. A control that sits under them
 * counts as "visible" and stays hidden. This nudges the scroll position just far
 * enough that the control lands in the unobscured region.
 */

/** Room left between the control and the sticky edge, so its focus ring shows. */
const REVEAL_MARGIN_PX = 4

const ROW_CONTAINER_SELECTOR =
  '[role="table"], [role="grid"], [role="treegrid"]'

function isStickyCell(element: Element): element is HTMLElement {
  return element instanceof HTMLElement && element.style.position === "sticky"
}

/** Whether the target rides inside a horizontally sticky cell, between it and `row`. */
function insideStickyCell(target: Element, row: HTMLElement): boolean {
  let node: Element | null = target
  while (node && node !== row) {
    if (isStickyCell(node)) {
      return true
    }
    node = node.parentElement
  }
  return false
}

/**
 * Scroll the table so `target` is not hidden under a pinned column or the
 * sticky header. `root` is the table's scroll container; `header` is its sticky
 * header rowgroup, when one renders. `platters` says the table scrolls
 * horizontally inside each platter and the floating header's own scroll region
 * rather than in `root`. A row outside all of them (a group band) is a single
 * full-width cell with nothing to scroll.
 */
export function revealFocusedControl(
  target: Element,
  root: HTMLElement,
  header: HTMLElement | null,
  { platters = false }: { platters?: boolean } = {},
): void {
  const row = target.closest<HTMLElement>('[role="row"]')
  // A row of a nested table or grid in a cell belongs to that widget, not to
  // this table, and its sticky cells say nothing about this scroll container.
  if (!row || row.closest(ROW_CONTAINER_SELECTOR) !== root) {
    return
  }
  const targetRect = target.getBoundingClientRect()

  // Horizontal: the row's sticky cells are the columns that paint over the
  // scrolling center. A control inside one of them never scrolls under itself,
  // and a focused row spans every column, so moving between rows keeps the
  // horizontal position the user scrolled to.
  const scroller = platters
    ? row.closest<HTMLElement>("[data-table-scroll-x]")
    : root
  if (scroller && target !== row && !insideStickyCell(target, row)) {
    const scrollerRect = scroller.getBoundingClientRect()
    let clearLeft = scrollerRect.left
    let clearRight = scrollerRect.right
    for (const cell of Array.from(row.children)) {
      if (!isStickyCell(cell)) {
        continue
      }
      const cellRect = cell.getBoundingClientRect()
      if (cell.style.left !== "") {
        clearLeft = Math.max(clearLeft, cellRect.right)
      } else if (cell.style.right !== "") {
        clearRight = Math.min(clearRight, cellRect.left)
      }
    }
    if (clearRight - clearLeft <= 2 * REVEAL_MARGIN_PX) {
      // Pins wider than the scrollport leave no clear region; no scroll
      // position uncovers the control, so keep the user's position.
    } else if (targetRect.left < clearLeft + REVEAL_MARGIN_PX) {
      scroller.scrollLeft -= clearLeft + REVEAL_MARGIN_PX - targetRect.left
    } else if (targetRect.right > clearRight - REVEAL_MARGIN_PX) {
      // A control wider than the clear region aligns its start instead, so
      // its label is what shows.
      const overflowEnd = targetRect.right - (clearRight - REVEAL_MARGIN_PX)
      const roomAtStart = targetRect.left - (clearLeft + REVEAL_MARGIN_PX)
      scroller.scrollLeft += Math.min(overflowEnd, roomAtStart)
    }
  }

  // Vertical: a body control scrolled up under the sticky header row.
  if (header && !header.contains(target)) {
    const headerBottom = header.getBoundingClientRect().bottom
    if (targetRect.top < headerBottom + REVEAL_MARGIN_PX) {
      root.scrollTop -= headerBottom + REVEAL_MARGIN_PX - targetRect.top
    }
  }
}
